import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Settings as SettingsIcon, User, Building2, Palette, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user, logout, refresh } = useAuth();
  const [biz, setBiz] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/business/me");
      setBiz(data);
      setForm(data);
    } catch { /* not onboarded */ }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/business/onboard", form);
      toast.success("Settings saved");
      await refresh();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2">Settings</div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Profile & brand</h1>
        <p className="text-white/50 mt-2 text-sm">Tune your account and business profile. AI uses this to personalise every generation.</p>
      </header>

      {/* Account */}
      <div className="glass rounded-2xl p-6 lg:p-8">
        <div className="flex items-center gap-2 mb-5">
          <User className="h-4 w-4 text-indigo-400" />
          <div className="text-xs uppercase tracking-[0.25em] text-white/40">Account</div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Info label="Name" value={user?.name} />
          <Info label="Email" value={user?.email} />
          <Info label="Role" value={user?.role} />
          <Info label="Member since" value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"} />
        </div>
        <button onClick={logout} data-testid="settings-logout" className="mt-6 inline-flex items-center gap-2 glass px-4 py-2.5 rounded-lg text-sm hover:bg-white/5">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>

      {/* Business profile (editable) */}
      {biz && (
        <div className="glass rounded-2xl p-6 lg:p-8">
          <div className="flex items-center gap-2 mb-5">
            <Building2 className="h-4 w-4 text-indigo-400" />
            <div className="text-xs uppercase tracking-[0.25em] text-white/40">Business profile</div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Business name" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} />
            <Field label="Industry" value={form.industry} onChange={(v) => setForm({ ...form, industry: v })} />
            <Field label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
            <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            <Field label="Website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
            <div className="md:col-span-2">
              <FieldLabel>Business description</FieldLabel>
              <textarea rows={2} value={form.business_description || ""} onChange={(e) => setForm({ ...form, business_description: e.target.value })} className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm" />
            </div>
            <div className="md:col-span-2">
              <FieldLabel>Target audience</FieldLabel>
              <textarea rows={2} value={form.target_audience || ""} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm" />
            </div>
          </div>

          <div className="mt-5 grid md:grid-cols-3 gap-4">
            <div>
              <FieldLabel>Preferred tone</FieldLabel>
              <select value={form.preferred_tone || "Professional"} onChange={(e) => setForm({ ...form, preferred_tone: e.target.value })} className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm">
                {["Professional", "Friendly", "Bold", "Luxury", "Playful", "Inspirational"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <FieldLabel>Posting frequency</FieldLabel>
              <select value={form.posting_frequency || "Daily"} onChange={(e) => setForm({ ...form, posting_frequency: e.target.value })} className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm">
                {["Daily", "3x per week", "Weekly", "Bi-weekly"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <FieldLabel>Brand color</FieldLabel>
              <div className="mt-2 flex items-center gap-3">
                <input type="color" value={form.brand_color || "#6366F1"} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="h-12 w-16 rounded-lg bg-transparent border border-white/10 cursor-pointer" />
                <span className="font-mono text-sm text-white/60">{form.brand_color}</span>
              </div>
            </div>
          </div>

          <button onClick={save} data-testid="settings-save" className="mt-6 brand-gradient px-5 py-3 rounded-lg text-sm font-semibold hover:opacity-90 glow-orange">
            Save changes
          </button>
        </div>
      )}

      <EmailPreferencesPanel />
    </div>
  );
}

function EmailPreferencesPanel() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/email/preferences").then((r) => setPrefs(r.data));
  }, []);

  const toggle = (cat) => {
    if (!prefs) return;
    const set = new Set(prefs.unsubscribed);
    if (set.has(cat)) set.delete(cat); else set.add(cat);
    setPrefs({ ...prefs, unsubscribed: Array.from(set) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/email/preferences", { unsubscribed: prefs.unsubscribed });
      setPrefs({ ...prefs, unsubscribed: data.unsubscribed });
      toast.success("Email preferences saved");
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  if (!prefs) return null;
  const unsubAll = prefs.unsubscribed.includes("marketing");

  return (
    <section className="glass rounded-2xl p-6 lg:p-8 mt-8" data-testid="email-prefs-panel">
      <h3 className="font-heading text-xl font-black mb-4">Email preferences</h3>
      <p className="text-sm text-white/60 mb-4">
        You'll always receive transactional emails (receipts, support replies, security alerts).
        Below are the optional categories you can mute.
      </p>
      <label className="flex items-start gap-3 p-3 glass rounded-lg cursor-pointer hover:bg-white/[0.04]" data-testid="prefs-marketing">
        <input type="checkbox" checked={unsubAll} onChange={() => toggle("marketing")} className="mt-1" />
        <div>
          <div className="font-semibold text-sm">Mute all marketing emails</div>
          <div className="text-xs text-white/50 mt-1">Product news, tips, feature launches, new-feature announcements. You can re-enable at any time.</div>
        </div>
      </label>
      <button onClick={save} disabled={saving} data-testid="prefs-save"
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
        {saving ? "Saving…" : "Save preferences"}
      </button>
      <div className="mt-5 text-xs text-white/40">
        Always-on: {prefs.transactional_always_sent.join(" · ")}
      </div>
    </section>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-white/40">{label}</div>
      <div className="text-sm mt-1">{value}</div>
    </div>
  );
}
function FieldLabel({ children }) { return <label className="text-xs uppercase tracking-[0.2em] text-white/40">{children}</label>; }
function Field({ label, value, onChange }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm" />
    </div>
  );
}
